// The three browsers' wordings for a fetch() that never reached the network:
// blocked by an extension or tracking protection, offline, DNS failure, or the
// tab navigating away mid-request.
const NETWORK_FETCH_FAILURE = /NetworkError when attempting to fetch resource|Failed to fetch|Load failed/;

/**
 * Sentry's `allowUrls` matches on stack-frame URLs, so it cannot filter an
 * event that has no stack at all. Firefox builds the TypeError for a failed
 * fetch() itself and hands it an empty stack, so every blocked third-party
 * request on the page — GTM and whatever tags it pulls in, Stripe.js —
 * collapses into a single anonymous group with nothing to act on
 * (ROULEZJEUNESSE-FRONT-3: 747 users, zero frames, 100% Firefox).
 *
 * This matches exactly that shape and nothing else: an unhandled rejection,
 * with no frames, whose value is a browser-generated network failure. An event
 * carrying even one frame still reports, as does anything we hand to
 * captureException ourselves.
 *
 * @param {object} event Sentry event
 * @returns {boolean} true when the event is unattributable network noise
 */
export function isUnattributableNetworkRejection(event) {
  const [exception] =
    (event && event.exception && event.exception.values) || [];
  if (!exception) return false;

  const frames = (exception.stacktrace && exception.stacktrace.frames) || [];

  return (
    frames.length === 0 &&
    exception.mechanism?.type === 'onunhandledrejection' &&
    exception.type === 'TypeError' &&
    NETWORK_FETCH_FAILURE.test(exception.value || '')
  );
}

// Le jeton public d'une demande faite depuis le site d'un réparateur
// (FIXERLIGHT-056) : il rattache la demande à un compte, il ne sort pas.
const JETON_DEMANDE_SITE = /(\/site\/bookings\/)[0-9a-f-]{36}/gi;

/**
 * What a breadcrumb may carry out of the site. Request URLs lose their query
 * string (GET params can carry personal data, see the extra.path scrub in
 * utils/api.jsx) and any site booking token in their path; navigation
 * breadcrumbs lose their fragment, where « Créer mon espace » receives that
 * token before clearing it with replaceState — which Sentry records.
 *
 * @param {object} breadcrumb Sentry breadcrumb
 * @returns {object} the breadcrumb, scrubbed when needed
 */
export function scrubBreadcrumb(breadcrumb) {
  const data = breadcrumb && breadcrumb.data;
  if (!data) return breadcrumb;

  if (
    (breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') &&
    typeof data.url === 'string'
  ) {
    const url = data.url.replace(JETON_DEMANDE_SITE, '$1[jeton]');
    return {
      ...breadcrumb,
      data: {
        ...data,
        url: breadcrumb.category === 'xhr' ? url.split('?')[0] : url,
      },
    };
  }

  if (breadcrumb.category === 'navigation') {
    const sansFragment = (v) => (typeof v === 'string' ? v.split('#')[0] : v);
    return {
      ...breadcrumb,
      data: {
        ...data,
        from: sansFragment(data.from),
        to: sansFragment(data.to),
      },
    };
  }

  return breadcrumb;
}

const masquerJeton = (v) =>
  typeof v === 'string' ? v.replace(JETON_DEMANDE_SITE, '$1[jeton]') : v;

/**
 * The same token, out of whole events: the page URL (`request.url`, fragment
 * included when the event starts before « Créer mon espace » clears it) and
 * the performance spans, which browserTracingIntegration names after each
 * fetch URL (`GET …/site/bookings/<token>`). Used for errors and transactions.
 *
 * @param {object} event Sentry event or transaction
 * @returns {object} the event, scrubbed
 */
export function scrubEvent(event) {
  if (!event) return event;
  let scrubbed = event;
  if (event.request && typeof event.request.url === 'string') {
    scrubbed = {
      ...scrubbed,
      request: {
        ...event.request,
        url: masquerJeton(event.request.url.split('#')[0]),
      },
    };
  }
  if (Array.isArray(event.spans)) {
    scrubbed = {
      ...scrubbed,
      spans: event.spans.map((span) => ({
        ...span,
        description: masquerJeton(span.description),
        ...(span.data
          ? {
              data: Object.keys(span.data).reduce(
                (acc, cle) => ({ ...acc, [cle]: masquerJeton(span.data[cle]) }),
                {},
              ),
            }
          : {}),
      })),
    };
  }
  return scrubbed;
}

/**
 * Where Sentry adds its sentry-trace + baggage headers (distributed tracing):
 * the production API, except `/v2/public/{slug}/site` and `…/site/events`
 * (FIXERLIGHT-056). Only those two answer every origin, for the script pasted
 * on repairers' sites, and their preflight allows only Accept and
 * Content-Type: an extra header gets the request blocked, and
 * /reserver/:slug can no longer read its workshop. The other `…/site/…`
 * routes go through the regular CORS and keep tracing.
 */
export const TRACE_PROPAGATION_TARGETS = [
  /^https:\/\/api\.roulezjeunesse\.com(?!\/v2\/public\/[^/]+\/site(?:\/events)?\/?(?:[?#]|$))/,
];

export default isUnattributableNetworkRejection;
